/** @format */

import type { Topic, TopicId } from 'src/types';

function buildTopicRelations(topicTree: Topic[] | null | undefined) {
  const topicMap = new Map<TopicId, Topic>();
  const parentMap = new Map<TopicId, TopicId>();

  const traverse = (topics: Topic[] = [], parentId: TopicId | null = null) => {
    topics.forEach(topic => {
      topicMap.set(topic.id, topic);
      if (parentId !== null) {
        parentMap.set(topic.id, parentId);
      }
      if (Array.isArray(topic?.children) && topic.children.length > 0) {
        traverse(topic.children, topic.id);
      }
    });
  };

  traverse(Array.isArray(topicTree) ? topicTree : []);
  return { topicMap, parentMap };
}

function collectDescendantIds(topic?: Topic | null): TopicId[] {
  if (!topic || !Array.isArray(topic.children) || topic.children.length === 0) {
    return [];
  }
  return topic.children.reduce<TopicId[]>((acc, childTopic) => {
    acc.push(childTopic.id);
    return [...acc, ...collectDescendantIds(childTopic)];
  }, []);
}

/**
 * Same parent/child sync semantics as NewsletterSection topic toggles.
 */
export function applyTopicToggle(
  selectedTopicIds: TopicId[] | null | undefined,
  topicTree: Topic[] | null | undefined,
  topicId: TopicId
): TopicId[] {
  const nextTopics = new Set(Array.isArray(selectedTopicIds) ? selectedTopicIds : []);
  const isChecked = nextTopics.has(topicId);
  const shouldCheck = !isChecked;
  const { topicMap, parentMap } = buildTopicRelations(topicTree);

  const syncParentState = (startingTopicId: TopicId) => {
    let currentParentId = parentMap.get(startingTopicId);

    while (currentParentId !== undefined && currentParentId !== null) {
      const parentTopic = topicMap.get(currentParentId);
      const childTopics = parentTopic?.children || [];
      const allChildrenChecked = childTopics.every(childTopic => nextTopics.has(childTopic.id));

      if (allChildrenChecked) {
        nextTopics.add(currentParentId);
      } else {
        nextTopics.delete(currentParentId);
      }

      currentParentId = parentMap.get(currentParentId);
    }
  };

  if (shouldCheck) {
    nextTopics.add(topicId);
  } else {
    nextTopics.delete(topicId);
  }

  const selectedTopic = topicMap.get(topicId);
  const descendantIds = collectDescendantIds(selectedTopic);

  descendantIds.forEach(descendantId => {
    if (shouldCheck) {
      nextTopics.add(descendantId);
    } else {
      nextTopics.delete(descendantId);
    }
  });

  syncParentState(topicId);
  return Array.from(nextTopics);
}
